import layout from './TreeView.html?raw';
import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';

import TextBoxT from '@/Controls/TextBox';
import TreeViewItemT from '@/Controls/TreeViewItem';
import FancyTreeControl from '@/Controls/FancyTreeControl';
import TreeViewViewModel from '@/ViewModels/Controls/TreeViewViewModel';
import TreeViewItemViewModel from '@/ViewModels/Controls/TreeViewItemViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';

const CONTROL_NAME = 'TreeView';

export default class TreeView extends Control {

    private TreeViewItem = ComponentManager.getControl<typeof TreeViewItemT>('TreeViewItem');
    private TextBox = ComponentManager.getControl<typeof TextBoxT>('TextBox');

    public fancyTreeControl: FancyTreeControl;

    protected control = null;

    public constructor(protected viewModel: TreeViewViewModel, protected element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(() => {
            this.applyBindings(this.control, {
                controlName: CONTROL_NAME,
                locator: this.viewModel.locator,
                parentLocator: this.viewModel.parentLocator
            });

            this.applyBindings(this.control.find('.panelbar-searchfield'), {
                visible: this.viewModel.hasSearchFeature
            });
        });
    }

    public getControl(): JQuery<HTMLElement> {
        if (this.control != null) {
            return this.control;
        }

        this.control = this.element || $('<div>');

        let timerId = this.applyTemplate(layout, this.control, this.applyTemplateCallback.bind(this));
        this.triggers[timerId].then(this.widget.bind(this, null));

        return this.control;
    }

    protected applyTemplateCallback() {
        let $controlChildren = this.control.children();
        let resource = ResourceManager.getInstance().loadResource('SEARCH', true);
        let $searchfield = $controlChildren.find('.panelbar-searchfield');
        let $resetField = $controlChildren.find('.reset-searchfield');
        let $collapseAllNodesField = $controlChildren.find('.collapse-all-nodes');
        let $input = $searchfield.children('input');

        $input.attr('placeholder', resource);

        this.addEventHandler('click', $resetField, () => {
            this.viewModel.searchString.value('');
        });

        this.addEventHandler('click', $collapseAllNodesField, () => {
            let fancytree = this.fancyTreeControl.getFancytree();
            if (fancytree) {
                const expand = this.isTreeCollapsed();
                fancytree.visit(node => {
                    if (expand && !this.viewModel.isInSearchMode()) {
                        this.showNode(node)
                    }
                    node.setExpanded(expand);
                });
                this.updateExpandIcon(expand)
            }
        });

        this.applyBindings($resetField, {
            locator: LocatorHelper.buildLocator(this.viewModel.locator, 'reset')
        });
        this.applyBindings($collapseAllNodesField, {
            locator: LocatorHelper.buildLocator(this.viewModel.locator, 'collapse')
        });
        this.applyBindings($input, {
            locator: LocatorHelper.buildLocator(this.viewModel.locator, 'search_input')
        });

        // add SearchFieldTextBox 
        let searchFieldTextBox = new this.TextBox(this.viewModel.searchString, $searchfield);
        searchFieldTextBox.getControl();

        // add FancyTree
        $.when(this.fancyTreeControl.widgetIsCreated).then(() => {
            this.fancyTreeControl.getControl();
        });

        this.fancyTreeControl.expand = (event: JQueryEventObject, data: Fancytree.EventData) => {
            let node = data.node;
            node.children.forEach(anyNode => {
                let treeViewItemViewModel = anyNode.data.treeViewItem as TreeViewItemViewModel;
                if (treeViewItemViewModel.isVisible()) {
                    this.showNode(anyNode);
                }
                else {
                    $(anyNode.li).hide();
                }
            });
        };

        this.fancyTreeControl.collapse = (event: JQueryEventObject, data: Fancytree.EventData) => {
            if (this.isTreeCollapsed()) {
                this.updateExpandIcon(false)
            }
            return true;
        }

        this.fancyTreeControl.beforeExpand = (event: JQueryEventObject, data: Fancytree.EventData) => {
            if (this.isTreeCollapsed()) {
                this.updateExpandIcon(true)
            }
            return true;
        };

        this.fancyTreeControl.renderNode = (event: JQueryEventObject, data: Fancytree.EventData) => {
            let node = data.node;
            let nodeData = node.data;

            if (nodeData['isRendered'] === true || _.keys(nodeData).length == 0) {
                return;
            }

            let $span = $(node.span);
            let $title = $span.find('> .fancytree-title');
            let $expander = $span.find('> .fancytree-expander');
            // Kein <li> verwenden, da das Element in den <span class="fancytree-title">
            // eingehängt wird -> axe-Regel "listitem" / WCAG 1.3.1.
            let $linkWrapper = $('<div>');
            $linkWrapper.appendTo($title);

            let treeViewItemViewModel = data.node.data.treeViewItem as TreeViewItemViewModel;
            let treeViewItem = new this.TreeViewItem(treeViewItemViewModel, $linkWrapper);
            let $treeViewItemControl = treeViewItem.getControl();

            this.defineSubscription(treeViewItemViewModel.isVisible, (isVisible) => {
                if (node.isVisible()) {
                    if (isVisible) {
                        this.showNode(node);
                    }
                    else {
                        $(node.li).hide();
                    }
                }
            })(treeViewItemViewModel.isVisible());

            this.applyBindings($expander, {
                locator: LocatorHelper.buildLocator(treeViewItemViewModel.locator, 'treeitem-toggle')
            });

            nodeData['isRendered'] = true;
        };

        this.fancyTreeControl.activate = (event: JQueryEventObject, data: Fancytree.EventData) => {
            let node = data.node;
            let nodeData = node.data;
            let treeViewItemViewModel = data.node.data.treeViewItem;

            node.setSelected(true);

            treeViewItemViewModel.onNavigate();
        };

        this.fancyTreeControl.select = (event: JQueryEventObject, data: Fancytree.EventData) => {
            let node = data.node;
            let nodeData = node.data;

            let tabbableChildren = $(node.span).find(':tabbable');
            if (tabbableChildren.length > 0) {
                tabbableChildren[0].focus();
            }
        };
    }

    private updateExpandIcon(expanded: boolean): void {
        let element = this.control.children().find(".collapse-all-nodes")
        if (expanded) {
            element.addClass("collapse-all-nodes-icon")
            element.removeClass("expand-all-nodes-icon")
        } else {
            element.removeClass("collapse-all-nodes-icon")
            element.addClass("expand-all-nodes-icon")
        }
    }

    private isTreeCollapsed(): boolean {
        let fancyTree = this.fancyTreeControl.getFancytree();
        for (let moduleNode of fancyTree.rootNode.children) {
            if (moduleNode.expanded) {
                return false;
            }
        }
        return true;
    }

    private showNode(node: Fancytree.FancytreeNode) {
        $(node.li).show();

        if (this.viewModel.isInSearchMode()) {
            let treeViewItemViewModel = node.data.treeViewItem as TreeViewItemViewModel;
            let hasVisibleChildren = treeViewItemViewModel.hasVisibleChildren();

            if (hasVisibleChildren) {
                if (treeViewItemViewModel.isVisible()) {
                    node.setExpanded(true);
                }
            }
        }
    }

}

ComponentManager.setControl('TreeView', TreeView, 'RADPresentation.HTMLClient.DefaultViewExtensions');